import {
  ClockCircleOutlined,
  DesktopOutlined,
  InfoCircleOutlined,
  OrderedListOutlined,
  PlayCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type {
  RdpRecordingEvent,
  RdpRecordingSession,
  RdpRecordingSessionView as RdpRecordingView,
} from "@p0security/shared/integrations/sessions/types";
import { Button, Empty, Flex, Modal, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import Hls from "hls.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getEnvironment } from "utils/environment";

import { getCurrentToken } from "../../../Login/tokens/TokenProvider";
import { JsonViewerModal } from "./JsonViewerModal";
import { SessionViewHeader } from "./SessionViewHeader";

const EVENT_COLORS: Record<string, string> = {
  clipboard: "purple",
  mouse_click: "gold",
  process_start: "blue",
  process_end: "default",
  session_start: "green",
  session_end: "red",
  window_focus: "orange",
  winlog: "volcano",
};

const renderEventDetail = (event: RdpRecordingEvent): string => {
  const d = (event.data ?? {}) as Record<string, unknown>;
  return String(d.message ?? event.type);
};

const formatOffset = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const getEventOffset = (
  event: RdpRecordingEvent,
  startedAt: string
): number => {
  const eventTime = new Date(event.ts).getTime();
  const sessionStart = new Date(startedAt).getTime();
  return Math.max(0, (eventTime - sessionStart) / 1000);
};

const statusColor = (status: RdpRecordingSession["status"]) => {
  switch (status) {
    case "completed":
      return "green";
    case "recording":
      return "blue";
    case "failed":
      return "red";
  }
};

const columns: ColumnsType<RdpRecordingSession> = [
  {
    title: "Target",
    dataIndex: "targetName",
    render: (name: string) => <Tag icon={<DesktopOutlined />}>{name}</Tag>,
  },
  {
    title: "User",
    dataIndex: "windowsUser",
    render: (user: string) => <Tag icon={<UserOutlined />}>{user}</Tag>,
  },
  {
    title: "Status",
    dataIndex: "status",
    render: (status: RdpRecordingSession["status"]) => (
      <Tag color={statusColor(status)}>{status}</Tag>
    ),
  },
  {
    title: "Duration",
    dataIndex: "durationSeconds",
    render: (secs: number | undefined) =>
      secs != null ? (
        <Tag icon={<ClockCircleOutlined />}>{formatOffset(secs)}</Tag>
      ) : (
        "—"
      ),
  },
  {
    title: "Started",
    dataIndex: "startedAt",
    render: (ts: string) => (ts ? new Date(ts).toLocaleString() : "—"),
  },
];

const buildRecordingBaseUrl = (
  orgSlug: string,
  requestId: string,
  recordingId: string
) => {
  const { apiUrl } = getEnvironment();
  return `${apiUrl}/o/${orgSlug}/sessions/${requestId}/recordings/${recordingId}`;
};

const RdpRecordingPlayer = ({
  recording,
  requestId,
}: {
  recording: RdpRecordingSession;
  requestId: string;
}) => {
  const { orgSlug } = useParams();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const eventListRef = useRef<HTMLDivElement>(null);
  const [activeEventIndex, setActiveEventIndex] = useState(-1);
  const [detailEvent, setDetailEvent] = useState<RdpRecordingEvent | null>(
    null
  );

  const handleDetailClose = useCallback(() => setDetailEvent(null), []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !orgSlug) return;

    // Clean up previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    let destroyed = false;

    const setup = async () => {
      const token = await getCurrentToken();
      if (destroyed || !token) return;

      const baseUrl = buildRecordingBaseUrl(orgSlug, requestId, recording.id);
      const hlsUrl = `${baseUrl}/hls/playlist.m3u8`;
      const videoUrl = `${baseUrl}/video`;

      if (Hls.isSupported()) {
        const hls = new Hls({
          maxBufferLength: 600,
          maxMaxBufferLength: 600,
          manifestLoadingTimeOut: 10000,
          manifestLoadingMaxRetry: 6,
          levelLoadingTimeOut: 10000,
          fragLoadingMaxRetry: 6,
          enableWorker: true,
          xhrSetup: (xhr) => {
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          },
        });
        hlsRef.current = hls;

        hls.loadSource(hlsUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              default:
                hls.destroy();
                hlsRef.current = null;
                break;
            }
          }
        });

        return;
      }

      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = videoUrl;
        video.addEventListener(
          "loadedmetadata",
          () => video.play().catch(() => {}),
          { once: true }
        );
      } else {
        video.src = videoUrl;
        video.load();
      }
    };

    setup();

    return () => {
      destroyed = true;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [orgSlug, requestId, recording.id]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !recording.startedAt) return;

    const handleTimeUpdate = () => {
      const time = video.currentTime;
      let idx = -1;
      for (let i = recording.events.length - 1; i >= 0; i--) {
        const offset = getEventOffset(
          recording.events[i]!,
          recording.startedAt
        );
        if (offset <= time) {
          idx = i;
          break;
        }
      }
      setActiveEventIndex(idx);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [recording.events, recording.startedAt]);

  useEffect(() => {
    if (activeEventIndex < 0 || !eventListRef.current) return;
    const el = eventListRef.current.querySelector(
      `[data-event-index="${activeEventIndex}"]`
    );
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeEventIndex]);

  const seekToEvent = useCallback(
    (event: RdpRecordingEvent) => {
      if (!videoRef.current || !recording.startedAt) return;
      videoRef.current.currentTime = getEventOffset(event, recording.startedAt);
    },
    [recording.startedAt]
  );

  const handleEventClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const index = Number(e.currentTarget.dataset.eventIndex);
      const event = recording.events[index];
      if (event) seekToEvent(event);
    },
    [recording.events, seekToEvent]
  );

  const handleDetailClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      const card =
        e.currentTarget.closest<HTMLDivElement>("[data-event-index]");
      if (!card) return;
      const index = Number(card.dataset.eventIndex);
      const event = recording.events[index];
      if (event) setDetailEvent(event);
    },
    [recording.events]
  );

  return (
    <Flex vertical gap="middle">
      <video
        ref={videoRef}
        controls
        style={{
          width: "100%",
          aspectRatio: "4 / 3",
          background: "#000",
          borderRadius: 4,
        }}
      />

      {recording.events.length > 0 && (
        <div
          ref={eventListRef}
          style={{
            maxHeight: 320,
            overflowY: "auto",
            border: "1px solid #303030",
            borderRadius: 4,
            padding: 8,
          }}
        >
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            Events ({recording.events.length})
          </Typography.Text>
          {recording.events.map((event, idx) => {
            const offset = recording.startedAt
              ? getEventOffset(event, recording.startedAt)
              : 0;
            const isActive = idx === activeEventIndex;
            return (
              <div
                key={idx}
                data-event-index={idx}
                onClick={handleEventClick}
                style={{
                  padding: "6px 8px",
                  marginBottom: 4,
                  borderRadius: 4,
                  cursor: "pointer",
                  background: isActive
                    ? "rgba(22, 119, 255, 0.15)"
                    : "transparent",
                  borderLeft: isActive
                    ? "3px solid #1677ff"
                    : "3px solid transparent",
                }}
              >
                <Flex gap="small" align="center">
                  <Typography.Text
                    type="secondary"
                    style={{
                      fontSize: 11,
                      flexShrink: 0,
                      fontFamily: "monospace",
                    }}
                  >
                    {formatOffset(offset)}
                  </Typography.Text>
                  <Tag
                    color={EVENT_COLORS[event.type] ?? "default"}
                    style={{ margin: 0, fontSize: 11, flexShrink: 0 }}
                  >
                    {event.type}
                  </Tag>
                  <Typography.Text
                    ellipsis
                    style={{ fontSize: 12, flex: 1, minWidth: 0 }}
                  >
                    {renderEventDetail(event)}
                  </Typography.Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<InfoCircleOutlined />}
                    onClick={handleDetailClick}
                    style={{ flexShrink: 0 }}
                  />
                </Flex>
              </div>
            );
          })}
        </div>
      )}
      <JsonViewerModal
        isOpen={!!detailEvent}
        onClose={handleDetailClose}
        data={detailEvent}
        title={
          detailEvent
            ? `${detailEvent.type} — ${formatOffset(recording.startedAt ? getEventOffset(detailEvent, recording.startedAt) : 0)}`
            : ""
        }
      />
    </Flex>
  );
};

const RdpRecordingEventList = ({
  recording,
}: {
  recording: RdpRecordingSession;
}) => {
  const [detailEvent, setDetailEvent] = useState<RdpRecordingEvent | null>(
    null
  );

  const handleDetailClose = useCallback(() => setDetailEvent(null), []);

  const handleDetailClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      const card =
        e.currentTarget.closest<HTMLDivElement>("[data-event-index]");
      if (!card) return;
      const index = Number(card.dataset.eventIndex);
      const event = recording.events[index];
      if (event) setDetailEvent(event);
    },
    [recording.events]
  );

  if (recording.events.length === 0) {
    return (
      <Empty
        description="No events recorded"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <>
      <div style={{ maxHeight: 480, overflowY: "auto" }}>
        {recording.events.map((event, idx) => {
          const offset = recording.startedAt
            ? getEventOffset(event, recording.startedAt)
            : 0;
          return (
            <div
              key={idx}
              data-event-index={idx}
              style={{
                padding: "6px 8px",
                marginBottom: 4,
                borderRadius: 4,
                borderLeft: "3px solid transparent",
              }}
            >
              <Flex gap="small" align="center">
                <Typography.Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    flexShrink: 0,
                    fontFamily: "monospace",
                  }}
                >
                  {formatOffset(offset)}
                </Typography.Text>
                <Tag
                  color={EVENT_COLORS[event.type] ?? "default"}
                  style={{ margin: 0, fontSize: 11, flexShrink: 0 }}
                >
                  {event.type}
                </Tag>
                <Typography.Text
                  ellipsis
                  style={{ fontSize: 12, flex: 1, minWidth: 0 }}
                >
                  {renderEventDetail(event)}
                </Typography.Text>
                <Button
                  type="text"
                  size="small"
                  icon={<InfoCircleOutlined />}
                  onClick={handleDetailClick}
                  style={{ flexShrink: 0 }}
                />
              </Flex>
            </div>
          );
        })}
      </div>
      <JsonViewerModal
        isOpen={!!detailEvent}
        onClose={handleDetailClose}
        data={detailEvent}
        title={
          detailEvent
            ? `${detailEvent.type} — ${formatOffset(recording.startedAt ? getEventOffset(detailEvent, recording.startedAt) : 0)}`
            : ""
        }
      />
    </>
  );
};

export const RdpRecordingSessionView = ({
  view,
  loading,
  getSession,
  requestId,
}: {
  view: RdpRecordingView;
  loading?: boolean;
  getSession?: () => void;
  requestId: string;
}) => {
  const [selectedRecording, setSelectedRecording] =
    useState<RdpRecordingSession | null>(null);
  const [modalMode, setModalMode] = useState<"events" | "play" | null>(null);

  const handlePlayClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const id = e.currentTarget.dataset.recordingId;
      const recording = view.sessions.find((s) => s.id === id);
      if (recording) {
        setSelectedRecording(recording);
        setModalMode("play");
      }
    },
    [view.sessions]
  );

  const handleEventsClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const id = e.currentTarget.dataset.recordingId;
      const recording = view.sessions.find((s) => s.id === id);
      if (recording) {
        setSelectedRecording(recording);
        setModalMode("events");
      }
    },
    [view.sessions]
  );

  const handleModalClose = useCallback(() => {
    setSelectedRecording(null);
    setModalMode(null);
  }, []);

  if (view.sessions.length === 0) {
    return (
      <>
        <SessionViewHeader
          view={view}
          fallbackTitle="RDP Session Recordings"
          loading={loading}
          onRefresh={getSession}
        />
        <Empty
          description="No RDP session recordings found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </>
    );
  }

  return (
    <>
      <SessionViewHeader
        view={view}
        fallbackTitle="RDP Session Recordings"
        loading={loading}
        onRefresh={getSession}
      />
      <Table
        dataSource={view.sessions}
        columns={[
          ...columns,
          {
            title: "",
            key: "actions",
            render: (_, recording) => (
              <Flex gap="small">
                <Button
                  type="primary"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  data-recording-id={recording.id}
                  onClick={handlePlayClick}
                >
                  Play
                </Button>
                <Button
                  size="small"
                  icon={<OrderedListOutlined />}
                  data-recording-id={recording.id}
                  onClick={handleEventsClick}
                  disabled={recording.eventCount === 0}
                >
                  Events
                </Button>
              </Flex>
            ),
          },
        ]}
        rowKey="id"
        size="small"
        pagination={false}
      />
      <Modal
        open={!!selectedRecording}
        onCancel={handleModalClose}
        footer={null}
        width={modalMode === "events" ? 720 : "80vw"}
        destroyOnClose
        title={
          selectedRecording && (
            <Flex gap="small" align="center">
              <Tag icon={<DesktopOutlined />}>
                {selectedRecording.targetName}
              </Tag>
              <Tag icon={<UserOutlined />}>{selectedRecording.windowsUser}</Tag>
              <Tag color={statusColor(selectedRecording.status)}>
                {selectedRecording.status}
              </Tag>
            </Flex>
          )
        }
      >
        {selectedRecording && modalMode === "play" && (
          <RdpRecordingPlayer
            recording={selectedRecording}
            requestId={requestId}
          />
        )}
        {selectedRecording && modalMode === "events" && (
          <RdpRecordingEventList recording={selectedRecording} />
        )}
      </Modal>
    </>
  );
};
